import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

/**
 * CalendarRedirect handles short URLs like /user/first-calendar
 * - If user is logged in as customer for this calendar -> redirect to /calendar/:symbolicName
 * - If user is not logged in -> redirect to /customer/login/:symbolicName
 * - If symbolic name doesn't exist -> redirect to /find-calendar
 */
const CalendarRedirect = () => {
  const navigate = useNavigate();
  const { symbolicName } = useParams();
  const { user, token, loading, getAuthHeaders, runningUser, isCustomer, isCalendarAdmin, isSuperAdmin } = useAuth();
  const [checking, setChecking] = useState(true);

  // Use runningUser for role checks (supports Run As mode)
  const effectiveUser = runningUser || user;

  useEffect(() => {
    const checkAndRedirect = async () => {
      // Wait for auth to finish loading
      if (loading) return;

      try {
        // First, verify the calendar exists
        const calendarResponse = await axios.get(`${API_BASE_URL}/public/calendars/${symbolicName}`);
        
        if (!calendarResponse.data?.data?.calendar) {
          // Calendar doesn't exist
          navigate('/find-calendar', { replace: true });
          return;
        }

        const calendar = calendarResponse.data.data.calendar;

        // Check if user is logged in
        if (token && effectiveUser) {
          // Check if user is a customer registered to this calendar
          if (isCustomer()) {
            try {
              // Verify customer is registered to this calendar
              const regResponse = await axios.get(
                `${API_BASE_URL}/calendar-registrations/my-calendar/details`,
                { headers: getAuthHeaders() }
              );
              
              if (regResponse.data?.data?.calendar?.id === calendar.id) {
                // Customer is registered to this calendar - go directly to calendar view
                navigate(`/calendar/${symbolicName}`, { replace: true });
                return;
              }
            } catch (err) {
              // Not registered or error - go to customer login
              console.log('Customer not registered to this calendar');
            }
          } else if (isCalendarAdmin() || isSuperAdmin()) {
            // Admin user - check if they own this calendar or are super admin
            if (calendar.owner_id === effectiveUser.id || isSuperAdmin()) {
              navigate(`/calendar/${symbolicName}`, { replace: true });
              return;
            }
          }
        }

        // Default: redirect to customer login for this calendar
        navigate(`/customer/login/${symbolicName}`, { replace: true });
        
      } catch (error) {
        console.error('Error checking calendar:', error);
        // Calendar not found or error - redirect to find calendar
        navigate('/find-calendar', { replace: true });
      } finally {
        setChecking(false);
      }
    };

    checkAndRedirect();
  }, [symbolicName, effectiveUser, token, loading, navigate, isCustomer, isCalendarAdmin, isSuperAdmin, getAuthHeaders]);

  // Show loading while checking
  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default CalendarRedirect;
